class WlsCartSlideOut extends HTMLElement {
  constructor() {
    super();

    this.isUpdating = false;

    this.config = {
      openButtonSelector: '.site-header__icon--cart',
      innerSelector: '.wls-cart-slide-out__inner',
      closeButtonSelector: '.js-cart-close',
      activeClass: 'is-active',
      bodyLockClass: 'prevent-scroller',
      emptyClass: 'wls-cart-slide-out--empty-cart',
    };

    this.handleOpen = this.handleOpen.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleOverlayClick = this.handleOverlayClick.bind(this);
    this.handleInnerClick = this.handleInnerClick.bind(this);
    this.handleInnerChange = this.handleInnerChange.bind(this);

    this.config.upsellSectionId = 'wls-cart-upsell';
    this.config.upsellTargetSelector = '[data-upsell-results]';
  }

  connectedCallback() {
    this.sectionId =
      this.getAttribute('data-section-id') ||
      this.getAttribute('section-id') ||
      null;

    this.body = document.body;
    this.inner = this.querySelector(this.config.innerSelector);
    this.openButton = document.querySelector(this.config.openButtonSelector);

    if (!this.inner || !this.openButton) return;

    this.openButton.addEventListener('click', this.handleOpen);
    document.addEventListener('keydown', this.handleKeydown);
    this.addEventListener('click', this.handleOverlayClick);

    this.inner.addEventListener('click', this.handleInnerClick);
    this.inner.addEventListener('change', this.handleInnerChange);
  }

  disconnectedCallback() {
    this.openButton?.removeEventListener('click', this.handleOpen);
    document.removeEventListener('keydown', this.handleKeydown);
    this.removeEventListener('click', this.handleOverlayClick);

    this.inner?.removeEventListener('click', this.handleInnerClick);
    this.inner?.removeEventListener('change', this.handleInnerChange);
  }

  /* =========================
     UI state
     ========================= */

  handleOpen(event) {
    event.preventDefault();
    this.open();
  }

  open() {
    this.classList.add(this.config.activeClass);
    this.body.classList.add(this.config.bodyLockClass);
    // Refresh first so drawer has the latest markup, then load upsell
    this.refresh().then(() => {
      const current = document.querySelector('wls-cart-slide-out.is-active') || document.querySelector('wls-cart-slide-out');
      current?.maybeLoadUpsell?.();
    });
  }

  close() {
    this.classList.remove(this.config.activeClass);
    this.body.classList.remove(this.config.bodyLockClass);
  }

  handleKeydown(event) {
    if (event.key === 'Escape' && this.classList.contains(this.config.activeClass)) {
      this.close();
    }
  }

  handleOverlayClick(event) {
    if (event.target.closest(this.config.closeButtonSelector)) {
      event.preventDefault();
      this.close();
      return;
    }

    if (!event.target.closest(this.config.innerSelector)) {
      this.close();
    }
  }

  /* =========================
     Cart interactions
     ========================= */

    handleInnerClick(event) {
      const removeButton = event.target.closest('.wls-cart-slide-out__item-remove');
      if (removeButton) {
        event.preventDefault();
        const item = removeButton.closest('.wls-cart-slide-out__item[data-line-key]');
        const key = item?.dataset?.lineKey;
        if (key) this.updateLineItem(key, 0);
        return;
      }
    
      const minusButton = event.target.closest('.wls-cart-slide-out__item-qty-minus');
      const plusButton = event.target.closest('.wls-cart-slide-out__item-qty-plus');
    
      if (minusButton || plusButton) {
        event.preventDefault();
    
        const item = event.target.closest('.wls-cart-slide-out__item');
        if (!item) return;
    
        const input = item.querySelector('input[name="quantity"]');
        if (!input) return;
    
        const current = parseInt(input.value, 10) || 1;
        const next = plusButton ? current + 1 : current - 1;
    
        input.value = String(next);
    
        // Cart line items: persist change via Ajax
        if (item.matches('[data-line-key]')) {
          const key = item.dataset.lineKey;
          this.updateLineItem(key, next);
        }
    
        // Upsell items: only update the input (no cart request yet)
        return;
      }
    
      const upsellButton = event.target.closest('.js-upsell-atc[data-variant-id]');
      if (upsellButton) {
        event.preventDefault();
    
        const variantId = upsellButton.getAttribute('data-variant-id');
        if (!variantId) return;
    
        const upsellItem = upsellButton.closest('.wls-cart-slide-out__item');
        const qtyInput = upsellItem?.querySelector('input[name="quantity"]');
    
        let quantity = parseInt(qtyInput?.value || '1', 10);
        if (Number.isNaN(quantity) || quantity < 1) quantity = 1;
    
        this.addVariantToCart(variantId, quantity);
      }
  }
    

  handleInnerChange(event) {
    const input = event.target.closest('input[name="quantity"]');
    if (!input) return;
  
    let quantity = parseInt(input.value, 10);
    if (Number.isNaN(quantity) || quantity < 1) quantity = 1;
    input.value = String(quantity);
  
    const item = input.closest('.wls-cart-slide-out__item[data-line-key]');
    if (!item) return;
  
    this.updateLineItem(item.dataset.lineKey, quantity);
  }
  

  /* =========================
     Shopify Ajax API
     ========================= */

  async updateLineItem(key, quantity) {
    if (this.isUpdating) return;
    this.isUpdating = true;
    this.setLoading(true);

    try {
      await this.fetchJSON('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });

      await this.refresh();
    } catch (error) {
      console.error('[Cart] Line update failed', error);
      await this.refresh().catch(() => {});
    } finally {
      this.isUpdating = false;
      this.setLoading(false);
    }
  }

  async addVariantToCart(variantId, quantity) {
    if (this.isUpdating) return;
    this.isUpdating = true;
    this.setLoading(true);
    try {
      await this.fetchJSON('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: Number(variantId), quantity }),
      });

      this.classList.add(this.config.activeClass);
      this.body.classList.add(this.config.bodyLockClass);

      await this.refresh();
    } catch (error) {
      console.error('[Cart] Add to cart failed', error);
      await this.refresh().catch(() => {});
    } finally {
      this.isUpdating = false;
      this.setLoading(false);
    }
  }

  /* =========================
     Cart refresh
     ========================= */

     async refresh() {
      if (!this.sectionId) return;
      const url =
      `/?section_id=wls-slideout-cart`;

      const html = await this.fetchHTML(url, { method: 'GET' });    
      if (!html) return;
    
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const freshCart = doc.querySelector('wls-cart-slide-out');
      if (!freshCart) return;

      const wasOpen = this.classList.contains(this.config.activeClass);

      const freshContent = freshCart.querySelector('.wls-cart-slide-out__content');
      const freshFooter = freshCart.querySelector('.wls-cart-slide-out__footer');
      if (!freshContent || !freshFooter) return;

      const currentContent = this.querySelector('.wls-cart-slide-out__content');
      const currentFooter = this.querySelector('.wls-cart-slide-out__footer');
      if (!currentContent || !currentFooter) return;

      // Replace only content + footer; keep header and outer element intact
      currentContent.replaceWith(freshContent);
      currentFooter.replaceWith(freshFooter);

      // Keep open state (since we didn't replace the root element)
      if (wasOpen) {
        this.classList.add(this.config.activeClass);
        document.body.classList.add(this.config.bodyLockClass);
      }

      await WlsCartSlideOut.updateCartBadgeFromCart();
      this.maybeLoadUpsell?.();

      const cart = await this.fetchCart();
      const isEmpty = !cart?.items || cart.items.length === 0;
      this.classList.toggle(this.config.emptyClass, isEmpty);

    }

    async maybeLoadUpsell() {
      const target = this.querySelector(this.config.upsellTargetSelector);
      if (!target) return;
    
      const cart = await this.fetchCart();
      const hasItems = !!cart?.items?.length;
    
      let seedProductId = null;
    
      if (hasItems) {
        seedProductId = cart.items[0]?.product_id || null;
      } else {
        const fallback = this.getAttribute('data-upsell-fallback-product-id');
        seedProductId = fallback ? Number(fallback) : null;
      }
    
      if (!seedProductId) {
        target.innerHTML = '';
        return;
      }
    
      await this.loadUpsell(seedProductId);
    }
    
    
    
    async loadUpsell(productId) {
      const target = this.querySelector(this.config.upsellTargetSelector);
      if (!target) return;
    
      try {
        const url =
          `/recommendations/products?section_id=${encodeURIComponent(this.config.upsellSectionId)}` +
          `&product_id=${encodeURIComponent(productId)}`;
    
        const res = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!res.ok) throw new Error(`Upsell request failed: ${res.status}`);
    
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
    
        const root = doc.querySelector('[data-upsell-section-root]');
        target.innerHTML = root ? root.innerHTML : html;
    
        window.__wlsUpsellLoadedForProductId = productId;
      } catch (error) {
        console.error('[Upsell] Load failed', error);
      }
    }
    
    async fetchCart() {
      try {
        const res = await fetch('/cart.js', {
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!res.ok) throw new Error(`Cart request failed: ${res.status}`);
        return await res.json();
      } catch (error) {
        console.error('[Cart] Fetch failed', error);
        return null;
      }
    }
    
    

  /* =========================
     Utilities
     ========================= */

  setLoading(isLoading) {
    this.classList.toggle('is-loading', isLoading);
  }
    
  async fetchJSON(url, options = {}) {
    const response = await fetch(url, {
      ...options,
      headers: {
        ...(options.headers || {}),
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    if (!response.ok) {
      throw new Error(`Request failed: ${response.status}`);
    }

    return response.json();
  }

  async fetchHTML(url, options = {}) {
    const res = await fetch(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      ...options,
      headers: {
        ...(options.headers || {}),
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'text/html',
      },
    });
  
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`HTML request failed: ${res.status} ${text.slice(0, 200)}`);
    }
  
    return res.text();
  }
  

  static async updateCartBadgeFromCart() {
      const badge =
        document.querySelector('.site-header__icon-num') ||
        document.querySelector('.js-cart-count');
    
      if (!badge) return;
    
      try {
        const cart = await fetch('/cart.js', {
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        }).then(r => r.json());
    
        const count = Number(cart?.item_count || 0);
    
        badge.textContent = String(count);
        badge.classList.toggle('is-hidden', count <= 0);
        badge.toggleAttribute('hidden', count <= 0);
      } catch (e) {
        console.error('[Cart] Failed to update badge', e);
      }
    }
    
}

if (!customElements.get('wls-cart-slide-out')) {
  customElements.define('wls-cart-slide-out', WlsCartSlideOut);
}
